import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Button,
  Icon,
  Flex,
  Badge,
  Card,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  Star,
  Compass,
  ArrowRight,
  GlobeHemisphereWest,
  UsersThree,
} from '@phosphor-icons/react'
import { useAuth } from '../../hooks/useAuth'
import { useTexture } from '../../context/EnhancedTextureContext';

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const communityFeatures = [
  {
    icon: UsersThree,
    colorToken: 'accent.nordic',
    title: 'Collaborative Research',
    description: 'Work together with experts and enthusiasts to document and analyze megalithic sites',
  },
  {
    icon: Star,
    colorToken: 'accent.egyptian',
    title: 'Recognition System',
    description: 'Earn reputation points and badges for your contributions and discoveries',
  },
  {
    icon: Compass,
    colorToken: 'accent.celtic',
    title: 'Guided Exploration',
    description: 'Access expert recommendations and curated collections of significant sites',
  },
];

export function CommunitySection() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { isTextureEnabled } = useTexture();

  const handleJoinCommunity = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/signup');
    }
  };

  return (
    <Box
      position="relative"
      bg={useColorModeValue('bg.default', 'bg.default')} // This will be the very back background color
      pt={{ base: 12, md: 16 }}
      pb={{ base: 6, md: 8 }}
      borderTop="1px solid"
      borderColor="border.light"
      overflow="hidden" // Important for pseudo-elements
      _before={{ // Leather texture - moved to outer Box
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: isTextureEnabled ? "url('/textures/leather.png')" : 'none',
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        opacity: isTextureEnabled ? useColorModeValue(0.1, 0.35) : 0,
        mixBlendMode: 'multiply', // Consider changing this if leather is too dark
        pointerEvents: 'none',
        zIndex: 0, // Behind Container, above Box's own bg
        transition: 'opacity 0.3s ease-in-out',
      }}
      _after={{ // Linen texture - moved to outer Box
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: isTextureEnabled ? "url('/textures/linen.png')" : 'none',
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        opacity: isTextureEnabled ? useColorModeValue(0.15, 0) : 0,
        mixBlendMode: 'overlay',
        pointerEvents: 'none',
        zIndex: 0, // Behind Container, above Box's own bg
        display: useColorModeValue('block', 'none'),
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      <Container
        variant="journal"
        maxW="container.xl"
        py={{ base: 8, md: 12 }}
        position="relative"
        zIndex={1} // Ensure container is above the Box's pseudo-elements
        bg={useColorModeValue("bg.surface", "bg.surface._dark")} 
        boxShadow="lg" 
        borderRadius="md" 
        // _before and _after pseudo-elements removed from here
      >
        <VStack spacing={16}>
          <VStack
            spacing={6} 
            textAlign="center"
            maxW="container.md"
            mx="auto"
            animation={`${fadeIn} 0.8s forwards`}
            sx={{
              '@media(prefers-reduced-motion: reduce)': {
                animation: 'none',
              }
            }}
          >
            <Flex 
              position="relative"
              mb={4}
              w="80px"
              h="80px"
              borderRadius="full"
              bgGradient="linear(to-br, bg.subtle, bg.surface)"
              justify="center"
              align="center"
              boxShadow="inner"
            >
              <Icon 
                as={GlobeHemisphereWest}
                fontSize="4xl" 
                color="interactive.primary" 
                opacity={0.8}
                boxShadow="sm"
              />
            </Flex>
            
            <Heading
              variant="journal" 
              size="2xl"
              position="relative"
              _after={{
                content: '""',
                position: 'absolute',
                bottom: '-15px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '3px',
                bgGradient: 'linear(to-r, accent.celtic, accent.egyptian)',
                borderRadius: 'full',
                opacity: 0.7
              }}
            >
              Research Community Network
            </Heading>
            
            <HStack spacing={2} wrap="wrap" justify="center" pt={4}>
              <Badge variant="journal">Global Network</Badge>
              <Badge variant="journal">Collaboration</Badge>
              <Badge variant="journal">Knowledge Sharing</Badge>
            </HStack>
            
            <Text 
              fontSize="xl" 
              maxW="3xl" 
              color="text.muted" 
              fontStyle="italic"
              pt={2}
            >
              Become part of a worldwide community of researchers, archaeologists, and enthusiasts dedicated to documenting and preserving our megalithic heritage.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
            {communityFeatures.map((item, index) => (
              <Card
                key={index}
                p={6}
                textAlign="center"
                variant="journal"
                boxShadow="md"
                position="relative"
                overflow="hidden"
                borderWidth="1px"
                borderColor="border.light"
                borderStyle="dashed"
                transition="all 0.3s ease"
                height="100%"
                bg={useColorModeValue('whiteAlpha.700', 'whiteAlpha.50')}
                _hover={{
                  transform: "translateY(-6px)",
                  boxShadow: "xl",
                  borderColor: "border.medium",
                }}
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '5px',
                  bgGradient: `linear(to-r, transparent, ${item.colorToken}, transparent)`,
                  opacity: 0.5,
                  zIndex: 0
                }}
              >
                
                <VStack spacing={6} position="relative" zIndex={1}>
                  <Flex
                    w="80px"
                    h="80px"
                    bgGradient="linear(to-br, bg.surface, bg.subtle)"
                    color={item.colorToken}
                    borderRadius="full"
                    justify="center"
                    align="center"
                    boxShadow="lg"
                    border="2px solid"
                    borderColor="border.light"
                    transition="all 0.3s ease"
                    _hover={{
                      transform: "scale(1.1) rotate(5deg)",
                      boxShadow: "xl"
                    }}
                  >
                    <Icon as={item.icon} fontSize="3xl" weight="duotone" />
                  </Flex>
                  <Heading
                    size="md" 
                    fontFamily="heading"
                    position="relative"
                    _after={{
                      content: '""',
                      position: 'absolute',
                      bottom: '-8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '40px',
                      height: '2px',
                      bg: item.colorToken,
                      opacity: 0.5,
                      borderRadius: 'full',
                    }}
                  >
                    {item.title}
                  </Heading>
                  <Text color="text.muted" fontFamily="body">
                    {item.description}
                  </Text>
                </VStack>
              </Card>
            ))}
          </SimpleGrid>

          <Box
            width="100%"
            textAlign="center"
            pt={10}
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: '30%',
              right: '30%',
              height: '1px',
              bgGradient: 'linear(to-r, transparent, border.default, transparent)',
              opacity: 0.5,
              zIndex: 0
            }}
          >
            <Button
              onClick={handleJoinCommunity}
              size="lg"
              variant="primary"
              px={8}
              py={6}
              rightIcon={<Icon as={ArrowRight} weight="bold" />}
              boxShadow="lg"
              position="relative"
              _hover={{
                transform: "translateY(-3px)",
                boxShadow: "xl"
              }}
              animation={`${pulse} 5s infinite ease-in-out`}
              sx={{
                '@media(prefers-reduced-motion: reduce)': {
                  animation: 'none',
                }
              }}
            >
              Access Research Platform
            </Button>
            
            <Text 
              fontSize="sm" 
              color="text.muted" 
              mt={4}
              fontFamily="handwritten"
              fontStyle="italic"
            >
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
